import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notification, Popover } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { AppRoutes } from '../../routes/routeConstants/appRoutes';
import { UserRole } from '../../enums/userEnums';
import './SideContainer.scss';
import ConditionalRender from '../../shared/components/ConditionalRender';
import { AllowedRoles } from '../../routes/routeConstants/allowedRoles';
import AssetManagerService from '../../services/AssetManager/assetManager.service';

interface ServiceLink {
  label: string;
  path: string;
  requiresAssetManager?: boolean;
}

const serviceLinks: ServiceLink[] = [
  { label: 'Map Portal', path: '/services/map-portal' },
  { label: 'Work Orders', path: '/services/assetmanager/work-orders', requiresAssetManager: true },
  { label: 'Contacts', path: '/services/assetmanager/contacts', requiresAssetManager: true },
  { label: 'Asset Overview', path: '/services/assetmanager/asset-overview', requiresAssetManager: true },
  { label: '3D Viewer', path: '/services/viewer' },
];

// Links absent from the map are open to every role.
const SERVICE_LINK_ROLES: Partial<Record<string, UserRole[]>> = {
  '/services/assetmanager/contacts': AllowedRoles.EDITOR_AND_ABOVE,
};

const SideContainer: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, canAccess, needsClientSelection, selectedClient } = useAuth();
  const { getStatus } = AssetManagerService();
  // Same cached status as AssetManagerGuard; links stay hidden until it confirms the add-on.
  const { data: assetManagerStatus } = useQuery({ ...getStatus(selectedClient?.id), enabled: !needsClientSelection });
  const visibleServiceLinks = serviceLinks.filter(
    ({ path, requiresAssetManager }) =>
      (!requiresAssetManager || assetManagerStatus?.enabled) && canAccess({ allowedRoles: SERVICE_LINK_ROLES[path] }),
  );

  const isHome = pathname.startsWith(AppRoutes.HOME);
  const isFiles = pathname.startsWith('/files');
  const isServices = pathname.startsWith('/services');
  const isClient = pathname === AppRoutes.SELECT_CLIENT;

  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleLogout = () => {
    setLogoutOpen(false);
    notification.destroy();
    logout();
  };

  const logoutContent = (
    <div className="app-rail__logout-confirm">
      <div className="app-rail__logout-header">
        <img src={'/assets/user-icon.svg'} alt="User" className="app-rail__logout-user-icon" />
        <span>LOG OUT?</span>
      </div>
      <button type="button" className="app-rail__logout-btn" onClick={handleLogout}>
        YES
      </button>
      <button type="button" className="app-rail__logout-btn" onClick={() => setLogoutOpen(false)}>
        NO
      </button>
    </div>
  );

  const servicesContent = (
    <div className="app-rail__flyout">
      {visibleServiceLinks.map((link) => (
        <button
          key={link.path}
          type="button"
          className={`app-rail__flyout-item${pathname.startsWith(link.path) ? ' is-active' : ''}`}
          onClick={() => navigate(link.path)}
        >
          {link.label}
        </button>
      ))}
    </div>
  );

  return (
    <nav className="app-rail">
      <div className="app-rail__items">
        <ConditionalRender visible={canAccess({ allowedRoles: AllowedRoles.ONLY_CES_ADMIN })}>
          <button type="button" className={`app-rail__item${isClient ? ' is-active' : ''}`} onClick={() => navigate(AppRoutes.SELECT_CLIENT)}>
            <img className="app-rail__icon" src={isClient ? '/assets/globe-active-icon.svg' : '/assets/globe-icon.svg'} alt="Client Selector" />
            <span className="app-rail__label">CLIENT</span>
          </button>
        </ConditionalRender>

        <ConditionalRender visible={!needsClientSelection}>
          <>
            <button type="button" className={`app-rail__item${isHome ? ' is-active' : ''}`} onClick={() => navigate(AppRoutes.HOME)}>
              <img className="app-rail__icon" src={isHome ? '/assets/home-selected.svg' : '/assets/home-icon.svg'} alt="Home" />
              <span className="app-rail__label">HOME</span>
            </button>

            <Popover content={servicesContent} trigger="click" placement="rightTop" overlayClassName="app-rail__popover" arrow={false}>
              <button type="button" className={`app-rail__item${isServices ? ' is-active' : ''}`}>
                <img
                  className="app-rail__icon"
                  src={isServices ? '/assets/services-icon-selected.svg' : '/assets/services-icon.svg'}
                  alt="Services"
                />
                <span className="app-rail__label">SERVICES</span>
              </button>
            </Popover>

            <button type="button" className={`app-rail__item${isFiles ? ' is-active' : ''}`} onClick={() => navigate('/files/all-files')}>
              <img className="app-rail__icon" src={isFiles ? '/assets/files-selected.svg' : '/assets/files-icon.svg'} alt="Files" />
              <span className="app-rail__label">FILES</span>
            </button>
          </>
        </ConditionalRender>
      </div>

      <Popover
        content={logoutContent}
        trigger="click"
        placement={'right'}
        overlayClassName="app-rail__popover app-rail__logout-popover"
        arrow={false}
        open={logoutOpen}
        onOpenChange={setLogoutOpen}
      >
        <button type="button" className="app-rail__item app-rail__logout">
          <img className="app-rail__icon" src={'/assets/logout-icon.svg'} alt="Logout" />
        </button>
      </Popover>
    </nav>
  );
};

export default SideContainer;
